import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, Clock, Flame, Inbox } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, StatCard, StatusBadge, Progress, Section, TimeLogDialog } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ClientIdentity } from "@/components/ClientBrand";
import { Clock3 } from "lucide-react";

export default function MyWork() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [timeTask, setTimeTask] = useState(null);
  const load = () => api.get("/my-work").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (t, status) => {
    try { await api.put(`/tasks/${t.id}`, { status }); toast.success(status === "done" ? "Nice — task completed" : `Moved to ${status.replace("_", " ")}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const claim = async (t) => {
    try { await api.put(`/tasks/${t.id}`, { assignee_id: user.id }); toast.success("Task assigned to you"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!d) return <div className="text-sm text-slate-500">Loading your work…</div>;
  const s = d.stats;

  return (
    <div data-testid="my-work-page">
      <PageHeader eyebrow={fmtDate(d.today)} title={`What's next, ${user.name.split(" ")[0]}`} subtitle="Your tasks sorted by urgency — overdue first, then priority, then due date. Work the list top to bottom." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 fade-up">
        <StatCard label="Open tasks" value={s.open} icon={Circle} testId="mw-stat-open" />
        <StatCard label="Overdue" value={s.overdue} tone={s.overdue ? "bad" : "default"} icon={Flame} testId="mw-stat-overdue" />
        <StatCard label="Due today" value={s.due_today} tone={s.due_today ? "warn" : "default"} icon={Clock} testId="mw-stat-today" />
        <StatCard label="Done this month" value={s.done_this_week} tone="good" icon={CheckCircle2} testId="mw-stat-done" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Section title="Next up" className="lg:col-span-2" testId="next-up-section">
          {!d.next_up.length && <p className="text-sm text-slate-500">You're all caught up. Pick something from the unassigned list.</p>}
          <div className="space-y-3">
            {d.next_up.map((t, i) => (
              <div key={t.id} className={`flex items-start gap-4 p-4 rounded-lg border ${i === 0 ? "border-blue-300 bg-blue-50/40" : "border-slate-200 bg-white"}`} data-testid={`next-task-${t.id}`}>
                <div className={`num text-xl font-semibold w-6 ${i === 0 ? "text-blue-600" : "text-slate-300"}`}>{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-medium text-slate-900">{t.title}</span><StatusBadge status={t.priority} />{t.overdue && <StatusBadge status="overdue" />}</div>
                  <div className="text-xs text-slate-500 mt-1"><Link to={`/projects/${t.project_id}`} className="hover:text-blue-600">{t.project_name}</Link> · due {fmtDate(t.due_date)} · {t.status.replace("_", " ")}{t.hours_logged ? ` · ${t.hours_logged}h logged` : ""}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setTimeTask(t)} title="Log time" data-testid={`log-time-${t.id}`}><Clock3 size={15} /></Button>
                  {t.status === "todo" && <Button size="sm" variant="outline" onClick={() => setStatus(t, "in_progress")} data-testid={`start-task-${t.id}`}>Start</Button>}
                  {t.status === "in_progress" && <Button size="sm" variant="outline" onClick={() => setStatus(t, "review")} data-testid={`review-task-${t.id}`}>To review</Button>}
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus(t, "done")} data-testid={`complete-task-${t.id}`}>Done</Button>
                </div>
              </div>
            ))}
          </div>
          {d.tasks.filter((t) => t.status !== "done").length > 5 && <Link to="/tasks" className="inline-block mt-4 text-sm text-blue-600 font-medium" data-testid="view-all-tasks-link">View all {d.tasks.filter((t) => t.status !== "done").length} open tasks on the board →</Link>}
        </Section>

        <div className="space-y-6">
          <Section title="My projects" testId="my-projects-section">
            <div className="space-y-3">
              {d.projects.map((p) => (
                <Link key={p.id} to={`/projects/${p.id}`} className="block group" data-testid={`my-project-${p.id}`}>
                  <div className="flex justify-between text-sm"><span className="font-medium truncate pr-2 group-hover:text-blue-600">{p.name}</span><span className="num text-xs text-slate-500">{p.progress}%</span></div>
                  <ClientIdentity clientId={p.client_id} name={p.client_name} className="text-xs text-slate-500 mt-1" size={24} testId={`my-project-client-${p.id}`} />
                  <Progress value={p.progress} className="mt-1.5" />
                </Link>
              ))}
              {!d.projects.length && <p className="text-sm text-slate-500">No projects assigned yet.</p>}
            </div>
          </Section>
          <Section title="Team goals" testId="team-goals-section">
            <div className="space-y-3">
              {d.goals.map((g) => (
                <div key={g.id}><div className="flex justify-between text-sm"><span className="truncate pr-2">{g.title}</span><span className="num text-xs text-slate-500">{g.progress}%</span></div><Progress value={g.progress} tone={g.progress >= 100 ? "bg-emerald-500" : "bg-blue-600"} className="mt-1.5" /></div>
              ))}
            </div>
          </Section>
        </div>
      </div>

      <Section title="Unassigned — pick one up" className="mt-6" testId="unassigned-section">
        {!d.unassigned.length && <p className="text-sm text-slate-500 flex items-center gap-2"><Inbox size={14} /> Nothing unassigned.</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {d.unassigned.map((t) => (
            <div key={t.id} className="border border-slate-200 rounded-lg p-4 flex items-start gap-3" data-testid={`unassigned-task-${t.id}`}>
              <div className="flex-1 min-w-0"><div className="font-medium text-sm truncate">{t.title}</div><div className="text-xs text-slate-500 mt-0.5">{t.project_name} · due {fmtDate(t.due_date)}</div><StatusBadge status={t.priority} className="mt-2" /></div>
              <Button size="sm" variant="outline" onClick={() => claim(t)} data-testid={`claim-task-${t.id}`}>Claim</Button>
            </div>
          ))}
        </div>
      </Section>
      <TimeLogDialog task={timeTask} open={!!timeTask} onOpenChange={(o) => !o && setTimeTask(null)} onLogged={load} user={user} />
    </div>
  );
}
